/**
 * How long the Ask tab waits for an answer that has not come (2026-09-27).
 *
 * Its own file, free of the API client, so the history and the screen can
 * share it and a test that stands in for `chat-api.ts` still has it.
 */
import { useEffect, useReducer } from "react";
import type { ChatMessage, ChatThread, QuestionSummary } from "@gb/contracts";

/**
 * How long a question may wait for its answer before the screen stops
 * waiting: the agent's 20 minutes, its wrap-up's 3 and 2 more, the span after
 * which the server's sweep stores a silent question as unfinished (§1.11 of
 * the Phase 5 design). The backoffice's `ANSWER_WAIT_MS` and the phone's
 * `WAIT_AT_MOST_MS` are the same span.
 */
export const ANSWER_WAIT_MS = 25 * 60_000;

type ThreadRead = { thread: ChatThread; messages: readonly ChatMessage[] };

/**
 * When the wait for this thread's answer ends, in epoch milliseconds, or null
 * when there is nothing to wait for: it is answered.
 *
 * **Counted from the question.** Some failures store no answer row: the old
 * path on any error, the agent when no model is configured, and a question
 * the sweep cannot find because it has no steps. Such a question used to read
 * as "still being answered" for as long as the tab stayed open, with two
 * requests every 2 seconds. A record that holds no question yet is counted
 * from the record, so the question the stream is about to write is still
 * read in.
 */
export function answerDeadline(data: ThreadRead | undefined): number | null {
  if (!data || data.messages.some((message) => message.role === "assistant")) return null;
  const asked = data.messages.find((message) => message.role === "user")?.createdAt ?? data.thread.createdAt;
  return deadlineFrom(asked);
}

/** Whether a thread's question waits for an answer that may still be running. */
export function awaitingAnswer(data: ThreadRead | undefined, now = Date.now()): boolean {
  const deadline = answerDeadline(data);
  return deadline !== null && now < deadline;
}

/**
 * Whether a question in the history may still be answered: it has no answer,
 * and was asked less than 25 minutes ago. The history said "No answer" for it
 * unless this tab's own stream was answering it, so after a reload, or from
 * another tab, a running question read as failed.
 */
export function stillAnswering(question: Pick<QuestionSummary, "answered" | "askedAt">, now = Date.now()): boolean {
  if (question.answered) return false;
  const deadline = deadlineFrom(question.askedAt);
  return deadline !== null && now < deadline;
}

/**
 * `awaitingAnswer`, for a screen: it turns false by itself when the wait runs
 * out, so a screen left open does not say "still being answered" past it.
 */
export function useAwaitingAnswer(data: ThreadRead | undefined): boolean {
  useRenderAt(answerDeadline(data));
  return awaitingAnswer(data);
}

/** `stillAnswering` for a page of the history: the ids, and it changes by itself when a wait runs out. */
export function useStillAnswering(questions: readonly QuestionSummary[] | undefined): ReadonlySet<string> {
  const now = Date.now();
  const waiting = (questions ?? []).filter((question) => stillAnswering(question, now));
  const deadlines = waiting.map((question) => deadlineFrom(question.askedAt) ?? now);
  useRenderAt(deadlines.length > 0 ? Math.min(...deadlines) : null);
  return new Set(waiting.map((question) => question.id));
}

function deadlineFrom(asked: string): number | null {
  const at = Date.parse(asked);
  return Number.isFinite(at) ? at + ANSWER_WAIT_MS : null;
}

/** Render again at `at`, in epoch milliseconds. */
function useRenderAt(at: number | null): void {
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (at === null) return;
    const left = at - Date.now();
    if (left <= 0) return;
    const timer = window.setTimeout(rerender, left);
    return () => window.clearTimeout(timer);
  }, [at]);
}
